import React from 'react';
import {connect} from 'react-redux';
import _ from 'lodash';
import {diffItem, createChangeObj, calculatePeriod} from 'common/requestDetails/utils';
import {produceAppliedDiff} from 'framework/collaboration/diff.utils';
import {produce} from 'immer';
import SimpleShared from 'go/components/modules/requestDetails/v3/items/shared/SimpleShared';
import {
  BOC,
  BOC_NAMES,
  DESCRIPTION,
  ACTUAL_PRICE,
  PRICE,
  ACTUAL_QUANTITY,
  QUANTITY,
  CHANGES_TYPES,
  UNIT_OF_MEASURE,
  PRE_AWARD,
  VALUE_CHANGE_FORMAT,
  EXPLAIN_OTHER,
  NAME,
  VALUE_CHANGE_DISPLAY,
  PHASED_PROJECT,
  CARD_TYPES,
  ADDITIONAL_INFORMATION,
  ACTUAL_UNIT_OF_MEASURE,
} from 'common/requestDetails/constants';

export class Simple extends React.Component {
  state = {
    lineItem: this.props.item,
    editMode: false,
  };

  toggleEditMode = () => this.setState({editMode: !this.state.editMode});

  handleChange = (lineItem, prevPropsItem, currentlyCollaborating) => {
    return new Promise((resolve) => {
      if (currentlyCollaborating) {
        if (!_.isEqual(prevPropsItem, lineItem)) {
          this.setState(
            (prevState) => ({
              lineItem: produceAppliedDiff(prevPropsItem, lineItem, prevState?.lineItem),
            }),
            () => resolve()
          );
        } else {
          resolve();
        }
      } else {
        this.setState({lineItem}, () => {
          resolve();
        });
      }
    });
  };

  componentDidUpdate = (prevProps) => {
    const lineItem = this.props.item;
    const prevPropsItem = prevProps.item;
    if (this.props.currentlyCollaborating && !_.isEqual(prevPropsItem, lineItem)) {
      this.setState((prevState) => ({
        lineItem: produceAppliedDiff(prevPropsItem, lineItem, prevState?.lineItem),
      }));
    }
  };

  toggleEditModeIneligibleDeferred = (value, editor) => {
    if (value && !editor) {
      this.toggleEditMode();
    }
  };

  handleBlur = (fieldName) => {
    if (this.props.editable) {
      let {lineItem} = this.state;
      let {item} = this.props;
      lineItem = _.cloneDeep(lineItem);
      let itemToDisplay = {
        ...lineItem,
        [ADDITIONAL_INFORMATION]: {
          ..._.get(item, ADDITIONAL_INFORMATION), // need to bring in additional information saved outside this component
          ..._.get(lineItem, ADDITIONAL_INFORMATION),
        },
      };

      this.setState((prevState) => ({
        lineItem: {
          ...prevState.lineItem,
          changes: itemToDisplay.changes,
          bocTotals: itemToDisplay.bocTotals,
        },
      }));
      this.props.saveItem(this.props.sourcePath, itemToDisplay, fieldName);
    }
  };

  handleItemChange = (name, value, ind = -1, extraArgs) => {
    let newLineItem = _.cloneDeep(this.props.item);
    _.set(newLineItem, name, value);
    newLineItem = this.getItemWithChanges(this.props.originalItem, newLineItem);
    const firstCostItemBOC = _.get(newLineItem, 'cost.0.0.boc');
    // If boc exists on first cost item, add it to each cost item in the array
    if (firstCostItemBOC) newLineItem.cost.reduce((a, c) => ((c[0].boc = a[0].boc), a));
    if (_.get(extraArgs, 'resetFields')) {
      _.each(extraArgs.resetFields, (field) => {
        _.unset(newLineItem, field);
      });
    }
    return this.handleChange(newLineItem);
  };

  handleItemChangeAndBlur = (name, value, fieldName) => {
    this.handleItemChange(name, value).then((res) => {
      this.handleBlur(fieldName);
    });
  };

  handleItemChangeAndEditMode = (name, value, fieldName) => {
    this.handleItemChangeAndBlur(name, value, fieldName);
    this.toggleEditModeIneligibleDeferred(value, this.state.editMode);
  };

  getItemWithChanges = (oldItem, newItemIn) => {
    const {itemConfig} = this.props;
    const period = calculatePeriod(itemConfig);

    let displayCostRow = _.get(oldItem, ['cost', 0], []).length > 1;
    let newItem = produce(newItemIn, (draft) => {
      return diffItem(
        draft,
        oldItem,
        _.get(itemConfig, 'labels'),
        this.props.itemCodeOptions
      );
    });

    let descriptionLabel = _.get(itemConfig, 'labels.description');
    let otherItemLabel = _.get(itemConfig, 'labels.otherDescription');
    let changes = [];
    let descriptionPath = 'additionalInformation.description';

    if (oldItem) {
      const findCost = (item, year, ind) => {
        return _.get(item, ['cost', year, ind], {});
      };

      if (!_.isEqual(_.get(oldItem, descriptionPath), _.get(newItem, descriptionPath))) {
        changes.push(
          createChangeObj(
            CHANGES_TYPES.VALUE_CHANGE_NO_DISPLAY,
            DESCRIPTION,
            undefined,
            undefined,
            descriptionLabel
          )
        );
      }

      if (!_.isEqual(_.get(oldItem, EXPLAIN_OTHER), _.get(newItem, EXPLAIN_OTHER))) {
        changes.push(
          createChangeObj(
            CHANGES_TYPES.VALUE_CHANGE,
            NAME,
            _.get(oldItem, EXPLAIN_OTHER),
            _.get(newItem, EXPLAIN_OTHER),
            otherItemLabel
          )
        );
      }

      _.forEach(_.get(newItem, 'cost', []), (phaseCost, phaseInd) => {
        _.forEach(phaseCost, (newCost, ind) => {
          const oldCost = findCost(oldItem, phaseInd, ind);
          const quantity = _.get(newCost, ACTUAL_QUANTITY);
          const price = _.get(newCost, ACTUAL_PRICE);
          const quantityOld = _.get(oldCost, QUANTITY);
          const priceOld = _.get(oldCost, PRICE);
          const added = _.get(newCost, 'added', false);
          let costRow = displayCostRow ? _.get(newCost, 'costRow', 0) : 0;
          if (added) {
            changes.push({
              ...createChangeObj(
                CHANGES_TYPES.NEWLY_CREATED,
                undefined,
                undefined,
                undefined,
                undefined
              ),
              costRow: costRow,
              periodType: period.type,
            });
          } else {
            if (_.toNumber(quantity) !== _.toNumber(quantityOld)) {
              let message = `cost ${ind + 1} ${_.upperFirst(period.type)} ${
                phaseInd + 1
              } ${QUANTITY}`;
              changes.push({
                ...createChangeObj(
                  CHANGES_TYPES.VALUE_CHANGE,
                  QUANTITY,
                  quantityOld,
                  quantity,
                  period.type !== 'whole' ? message : VALUE_CHANGE_DISPLAY[QUANTITY]
                ),
                costRow: costRow,
                periodType: period.type,
              });
            }
            if (_.toNumber(price) !== _.toNumber(priceOld)) {
              let message = `cost ${ind + 1} ${_.upperFirst(period.type)} ${
                phaseInd + 1
              } ${PRICE}`;

              changes.push({
                ...createChangeObj(
                  CHANGES_TYPES.VALUE_CHANGE,
                  PRICE,
                  priceOld,
                  price,
                  period.type !== 'whole' ? message : VALUE_CHANGE_DISPLAY[PRICE]
                ),
                costRow: costRow,
                periodType: period.type,
              });
            }

            let boc = _.get(newCost, BOC);
            let bocOld = _.get(oldCost, BOC);

            if (boc !== bocOld) {
              changes.push({
                ...createChangeObj(
                  CHANGES_TYPES.VALUE_CHANGE,
                  BOC,
                  BOC_NAMES[bocOld],
                  BOC_NAMES[boc]
                ),
                costRow: costRow,
                periodType: period.type,
              });
            }
            const unitOfMeasure = _.get(newCost, ACTUAL_UNIT_OF_MEASURE);
            const unitOfMeasureOld = _.get(oldCost, UNIT_OF_MEASURE);

            if (unitOfMeasure !== unitOfMeasureOld) {
              changes.push({
                ...createChangeObj(
                  CHANGES_TYPES.VALUE_CHANGE,
                  UNIT_OF_MEASURE,
                  unitOfMeasureOld,
                  unitOfMeasure
                ),
                costRow: costRow,
                periodType: period.type,
              });
            }

            const preAward = _.get(newCost, PRE_AWARD, false);
            const preAwardOld = _.get(oldCost, PRE_AWARD, false);

            if (preAward !== preAwardOld) {
              changes.push({
                ...createChangeObj(
                  CHANGES_TYPES.VALUE_CHANGE,
                  PRE_AWARD,
                  preAwardOld,
                  preAward
                ),
                costRow: costRow,
                periodType: period.type,
                formatType: VALUE_CHANGE_FORMAT.preAward,
              });
            }

            const phasedProject = _.get(newCost, PHASED_PROJECT, false);
            const phasedProjectOld = _.get(oldCost, PHASED_PROJECT, false);

            if (phasedProject !== phasedProjectOld) {
              changes.push({
                ...createChangeObj(
                  CHANGES_TYPES.VALUE_CHANGE,
                  PHASED_PROJECT,
                  phasedProjectOld,
                  phasedProject
                ),
                costRow: costRow,
                periodType: period.type,
              });
            }
          }
        });
      });
    }

    let updatedItem = produce(newItem, (draft) =>
      _.set(
        draft,
        [ADDITIONAL_INFORMATION, 'changes'],
        _.concat(_.get(newItem, 'additionalInformation.changes'), changes)
      )
    );

    return updatedItem;
  };

  renderChangesSection = () => {
    return <React.Fragment />;
  };

  render() {
    return (
      <SimpleShared
        lineItem={this.state.lineItem}
        editMode={this.state.editMode}
        costType={CARD_TYPES.SIMPLE}
        toggleEditMode={this.toggleEditMode}
        handleItemChangeAndEditMode={this.handleItemChangeAndEditMode}
        renderChangesSection={this.renderChangesSection}
        handleChange={this.handleChange}
        handleBlur={this.handleBlur}
        handleItemChange={this.handleItemChange}
        handleItemChangeAndBlur={this.handleItemChangeAndBlur}
        handleIneligibleItemChange={undefined}
        handleIneligibleItemChangeAndBlur={undefined}
        {...this.props}
      />
    );
  }
}

export const mapStateToProps = (state, props) => {
  const currentlyCollaborating = !!_.get(
    state,
    'dynamicComponent.collaboration.roomList',
    ''
  );
  return {currentlyCollaborating};
};

export default connect(mapStateToProps, null)(Simple);
